<script setup>
import timezones from '@/api/timezones';
import useEventData from '@/composables/useEventData';
import { displayTimeOffsetUtil } from '@/utils/time';
import { DateTime } from 'luxon';
import { Mask } from 'maska';
import { storeToRefs } from 'pinia';
import useEventDataStore from '../stores/eventDataStore';
import useFeatureFlagStore from '~/stores/featureFlagStore.js';
import EventDateTimeLocation from '../dashboards/src/components/EventDateTimeLocation.vue';
import BaseUrl from '~/api/BaseUrl.js';
import ENDPOINTS from '~/api/endpoints/index.js';
import Headers from '~/api/Headers.js';
import isEqual from 'lodash/isEqual.js';

const mask = new Mask({ mask: '(###) ###-####' });

const router = useRouter();
const route = useRoute();
const { eventData: stashedEventData, error: errorLoadingData } = storeToRefs(useEventDataStore());
const eventDataStore = useEventDataStore();

const { data, error: apiError } = await useEventData(route.params.id);
eventDataStore.setEventData(data?.value ?? {}, apiError?.value ?? {});

const eventData = ref(data?.value ?? stashedEventData);
const eventTimeZone = ref(null);

watch(
  eventData,
  (newValue) => {
    eventTimeZone.value = DateTime.fromISO(newValue?.event_start_date)
      .setZone(newValue?.event_timezone)
      .toFormat('ZZZZ');
  },
  { immediate: true },
);

useHead({
  title: `${data?.value?.event_title || 'FundEasy'}`,
  meta: [
    {
      property: 'og:title',
      content: data?.value?.event_title,
    },
    { property: 'og:image', content: data?.value?.facebook_banner_image },
    { property: 'twitter:card', content: 'summary' },
  ],
});

let apiEnvironment = '';

switch (true) {
  case window?.location.hostname.includes('v4app'):
    apiEnvironment = 'production';
    break;
  case window?.location.hostname.includes('beta'):
    apiEnvironment = 'beta';
    break;
  case window?.location.hostname.includes('qa'):
    apiEnvironment = 'qa';
    break;
  case window?.location.hostname.includes('staging'):
    apiEnvironment = 'staging';
    break;
  default:
    apiEnvironment = 'localhost';
}

const flagDataStore = useFeatureFlagStore();
const { flagData, error: errorLoadingFlagData } = storeToRefs(flagDataStore);

const featureFlagsUrl = computed(
  () =>
    `${BaseUrl.get(ENDPOINTS.META.SETTINGS)}${
      ENDPOINTS.META.SETTINGS
    }?id=${apiEnvironment}_frontend&event_id=${route.params.id}`,
);

const {
  data: featureFlags,
  error: errorGettingFeatureFlags,
  execute: executeFlags,
} = await useFetch(
  featureFlagsUrl,
  {
    headers: Headers,
  },
  {
    key: Date.now(),
  },
);
await executeFlags();

if ((!flagData?.value && featureFlags) || !isEqual(featureFlags?.value, flagData?.value)) {
  nextTick(() =>
    flagDataStore?.setFlagsData(featureFlags?.value ?? {}, errorGettingFeatureFlags?.value ?? {}),
  );
}

const handleRegistrationClick = () => {
  if (eventData?.registration_open) {
    router.push({
      name: 'peer-id-ticket-selection',
      params: { id: route.params.id },
    });
  } else {
    router.push({
      name: 'peer-id-registration-closed',
      params: { id: route.params.id },
    });
  }
};
</script>

<template>
  <v-container
    class="pb-0 pt-0"
    :style="{
      backgroundColor: eventData?.event_color || 'none',
      position: 'relative',
      margin: 0,
      maxWidth: '100vw',
      minHeight: '100%',
      overflow: 'auto',
    }"
    v-if="eventData !== undefined && eventData !== null && Object.keys(eventData)?.length > 0"
  >
    <v-row no-gutters style="height: 100%; width: 100%" justify="center">
      <v-col sm="12" md="10" :style="`background-color:#E5E7EB; position:relative`">
        <div
          class="backdrop"
          :style="{
            backgroundImage: `linear-gradient(rgba(211, 211, 211, 0.45), rgba(211, 211, 211, 0.45)),url(${eventData?.banner_image})`,
          }"
        />
        <main
          class="w-100 d-flex flex-column"
          style="position: relative; top: 0; padding: 7.6% 9.82%; overflow: auto"
        >
          <div class="w-100" style="height: 400px; min-height: 400px">
            <img :src="eventData?.banner_image" style="height: 100%; width: 100%" />
          </div>

          <v-row
            class="mb-6"
            no-gutters
            style="background-color: var(--fundeasy-colors-generic-00)"
          >
            <v-col style="padding: 48px">
              <v-row
                style="margin-bottom: 48px"
                no-gutters
                v-if="route.name !== 'peer-id-invitation-confirmation'"
              >
                <v-col>
                  <v-row
                    no-gutters
                    :justify="
                      route.name === 'peer-id-confirmation-cartId' ||
                      route.name === 'peer-id-invitation' ||
                      route.name === 'peer-id-invitation-invitationId-confirmation' ||
                      route.name === 'peer-id-invitation-invitationId' ||
                      route.name === 'peer-id-registration-closed'
                        ? 'center'
                        : 'start'
                    "
                  >
                    <img
                      class="mr-4"
                      style="width: 25px; height: 25px"
                      :src="
                        eventData?.account_logo ? eventData?.account_logo : '/assets/FE_icon.svg'
                      "
                      alt="Account Logo"
                    />
                    <p style="font-size: 0.875rem; color: var(--fundeasy-colors-neutral-500)">
                      {{ eventData?.account_name }}
                    </p>
                  </v-row>
                  <v-row
                    no-gutters
                    :justify="
                      route.name === 'peer-id-confirmation-cartId' ||
                      route.name === 'peer-id-invitation' ||
                      route.name === 'peer-id-invitation-invitationId-confirmation' ||
                      route.name === 'peer-id-invitation-invitationId' ||
                      route.name === 'peer-id-registration-closed'
                        ? 'center'
                        : 'start'
                    "
                    class="my-2"
                  >
                    <h1 style="font-size: 2.25rem">{{ eventData?.event_title }}</h1>
                  </v-row>
                  <v-row
                    no-gutters
                    :justify="
                      route.name === 'peer-id-confirmation-cartId' ||
                      route.name === 'peer-id-invitation' ||
                      route.name === 'peer-id-invitation-invitationId-confirmation' ||
                      route.name === 'peer-id-invitation-invitationId' ||
                      route.name === 'peer-id-registration-closed'
                        ? 'center'
                        : 'start'
                    "
                    v-if="route.name !== 'peer-id-invitation'"
                  >
                    <EventDateTimeLocation
                      :startDate="eventData?.event_start_date"
                      :endDate="eventData?.event_end_date"
                      :timezone="eventData?.event_timezone"
                      :isVirtual="eventData?.location_details?.isVirtual"
                      :city="eventData?.location_details?.city"
                      :state="eventData?.location_details?.state"
                    />
                  </v-row>
                </v-col>
              </v-row>
              <slot v-if="!errorLoadingData" />
            </v-col>
            <!-- Master of Ceremony -->
            <v-col sm="12" md="4" v-if="$route.name === 'peer-id-event'">
              <aside class="pt-md-12 pr-md-12 pa-sm-12">
                <v-row no-gutters class="mb-4" justify="center">
                  <v-btn
                    elevation="1"
                    @click="handleRegistrationClick"
                    class="w-100 text-transform-none"
                    variant="elevated"
                    :color="eventData?.value?.event_color ?? eventData?.event_color ?? 'info'"
                    style="color: var(--fundeasy-colors-generic-00); height: 48px"
                    >Register For Event</v-btn
                  >
                </v-row>
                <v-divider class="mb-4"></v-divider>
                <v-row no-gutters>
                  <v-col
                    style="border: 1px solid #cacaca"
                    class="pa-3"
                    v-if="
                      eventData?.promotional_information?.speaker_name ||
                      eventData?.promotional_information?.speaker_description ||
                      eventData?.promotional_information?.master_of_ceremony_name
                    "
                  >
                    <div v-if="eventData?.promotional_information?.speaker_name">
                      <p
                        class="mb-3"
                        style="color: var(--fundeasy-colors-neutral-500); font-weight: bold"
                      >
                        Speaker
                      </p>
                      <v-divider class="mb-4"></v-divider>
                      <p class="mb-4">{{ eventData?.promotional_information?.speaker_name }}</p>
                    </div>
                    <div
                      class="mb-4"
                      v-html="eventData?.promotional_information?.speaker_description"
                    ></div>
                    <div v-if="eventData?.promotional_information?.master_of_ceremony_name">
                      <p
                        class="mb-3"
                        style="color: var(--fundeasy-colors-neutral-500); font-weight: bold"
                      >
                        Master of Ceremonies
                      </p>
                      <v-divider class="mb-4"></v-divider>
                      <p>{{ eventData?.promotional_information?.master_of_ceremony_name }}</p>
                    </div>
                  </v-col>
                </v-row>
              </aside>
            </v-col>

            <v-col style="width: fit-content; max-width: fit-content" v-if="$slots['main-aside']">
              <slot name="main-aside"></slot>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            style="background-color: var(--fundeasy-colors-generic-00)"
            class="pl-12 pr-12 pt-4 pb-4"
          >
            <v-col>
              <a
                v-if="eventData?.promotional_information?.org_website"
                :href="eventData?.promotional_information?.org_website"
                target="_blank"
                >{{ eventData?.promotional_information?.org_website }}</a
              >
              <p>
                For help, contact
                {{ eventData?.point_of_contact?.primary_event_contact_first_name }}
                {{ eventData?.point_of_contact?.primary_event_contact_last_name }} at

                {{
                  mask.masked(
                    String(eventData?.point_of_contact?.primary_event_contact_phone_number),
                  )
                }}
                <span v-if="eventData?.point_of_contact?.primary_event_contact_phone_ext">
                  x{{ eventData?.point_of_contact?.primary_event_contact_phone_ext }}
                </span>
                or
                <a :href="`mailto:${eventData?.point_of_contact?.primary_event_contact_email}`">{{
                  eventData?.point_of_contact?.primary_event_contact_email
                }}</a>
              </p>
            </v-col>
            <v-col sm="4">
              <v-row no-gutters justify="end" align="center">
                <img
                  src="/peer_to_peer_logo.png"
                  alt="Peer To Peer logo"
                  style="width: 130px; height: auto"
                />
              </v-row>
            </v-col>
          </v-row>
        </main>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.backdrop {
  min-height: 400px;
  height: 400px;
  min-width: 100%;
  position: absolute;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  filter: blur(8px);
  -webkit-filter: blur(8px);
  z-index: 0;
  top: 0;
  left: 0;
}
</style>
